import {
  Account,
  AccountUser,
  ClubAkahuConnection,
  TournamentPaymentBy,
  TournamentPaymentMethod,
  TournamentSession,
  TournamentSessionStatus,
} from "@mixitone/models";
import { List } from "@mixitone/oom";
import { FormController } from "components/Forms/FormController";
import TournamentController from "components/Pages/Tournament/TournamentController";
import dayjs from "dayjs";
import { startClubAkahuOnboarding } from "lib/akahu/startClubAkahuOnboarding";
import { isMfaSessionAbortError, requireMfaSession } from "lib/mfa/requireMfaSession";
import { messagesService } from "services";
import { getAkahuClubSetupCopy } from "../../../../payments/src/akahu/copy";
import { getPaymentMethodsByRegion } from "../../../../payments/src/methods";
import type { FormFieldConfig } from "./FormFieldsDefinition";

interface TournamentSessionState {
  date: string;
  paymentMethods: List<TournamentPaymentMethod>;
  showPaymentMethodModal: boolean;
  selectedPaymentMethod: TournamentPaymentMethod | null;
  selectedPaymentType: string | null;
  selectedPaymentMethodId: string | null;
  clubAkahuConnection: ClubAkahuConnection | null;
  loadingClubAkahuConnection: boolean;
  startingAkahuOnboarding: boolean;
}

export class TournamentSessionFormController extends FormController<
  TournamentSession,
  {
    club_id?: string;
    name?: string;
  },
  TournamentSessionState
> {
  static override initialState = {
    ...FormController.initialState,
    date: "",
    paymentMethods: new List<TournamentPaymentMethod>(),
    showPaymentMethodModal: false,
    selectedPaymentMethod: null,
    selectedPaymentType: null,
    selectedPaymentMethodId: null,
    clubAkahuConnection: null,
    loadingClubAkahuConnection: false,
    startingAkahuOnboarding: false,
  };

  override async buildRecord(): Promise<TournamentSession> {
    this.state.date = dayjs().add(1, "month").set("hour", 9).format("YYYY-MM-DDTHH:mm");

    return new TournamentSession({
      courts: 5,
      stand_down_minutes: 10,
      ...this.props.attributes,
      starts_at: dayjs().add(1, "month").set("hour", 9).set("minute", 0).toISOString(),
      ends_at: dayjs().add(1, "month").set("hour", 17).set("minute", 0).toISOString(),
      registration_closes_at: dayjs().add(2, "weeks").set("hour", 21).set("minute", 0).toISOString(),
      status: "setup",
    });
  }

  override async onLoad() {
    await super.onLoad();

    if (!this.state.newRecord) {
      // Load payment methods for existing tournament session
      const paymentMethods = await TournamentPaymentMethod.query()
        .eq("tournament_session_id", this.state.record.id)
        .eq("is_deleted", false)
        .all();

      this.state.paymentMethods = new List(...paymentMethods);
      this.syncPaymentMethodsToTournament();
    }

    await this.loadClubAkahuConnection();
  }

  private syncPaymentMethodsToTournament() {
    try {
      const tournament = this.get(TournamentController);
      tournament.state.paymentMethods.setItems([...this.state.paymentMethods]);
      tournament.setupModelRelations();
    } catch {
      this.state.record.paymentMethods.set([...this.state.paymentMethods]);
    }
  }

  async loadClubAkahuConnection() {
    const clubId = this.state.record.club_id;
    if (
      !clubId ||
      Account.current?.region !== "nz" ||
      !Account.current?.hasFeature("akahu_tournament_payments")
    ) {
      this.state.clubAkahuConnection = null;
      this.state.loadingClubAkahuConnection = false;
      return;
    }

    this.state.loadingClubAkahuConnection = true;
    try {
      const connection = await ClubAkahuConnection.query()
        .eq("club_id", clubId)
        .eq("is_deleted", false)
        .orderBy("updated_at", "desc")
        .first();
      this.state.clubAkahuConnection = connection ?? null;
    } catch (error) {
      this.state.clubAkahuConnection = null;
      messagesService.addMessage(
        error instanceof Error ? error.message : "Failed to load bank connection",
        "error",
      );
    } finally {
      this.state.loadingClubAkahuConnection = false;
    }
  }

  get hasAkahuTournamentPayments(): boolean {
    return (
      Account.current?.region === "nz" && Account.current?.hasFeature("akahu_tournament_payments") === true
    );
  }

  get hasAkahuEnduring(): boolean {
    return Account.current?.hasFeature("akahu_enduring") === true;
  }

  get needsAkahuConnection(): boolean {
    return this.hasAkahuTournamentPayments && this.state.clubAkahuConnection?.isConnected !== true;
  }

  get hasAkahuPaymentMethod(): boolean {
    return this.state.paymentMethods.some((method) => method.payment_type === "akahu-one-off");
  }

  async actionStartAkahuOnboarding() {
    const clubId = this.state.record.club_id;
    const tournamentId = this.state.record.id;
    if (!clubId || !tournamentId) {
      messagesService.addMessage("Tournament club is missing", "error");
      return;
    }

    this.state.startingAkahuOnboarding = true;
    try {
      const copy = getAkahuClubSetupCopy(this.hasAkahuEnduring);
      await requireMfaSession({
        reason: copy.mfaReason,
        returnPath: `/tournament/${tournamentId}`,
      });
      await startClubAkahuOnboarding({
        clubId,
        returnPath: `/tournament/${tournamentId}`,
      });
    } catch (error) {
      this.state.startingAkahuOnboarding = false;
      if (!isMfaSessionAbortError(error)) {
        messagesService.addMessage(
          error instanceof Error ? error.message : getAkahuClubSetupCopy(this.hasAkahuEnduring).startError,
          "error",
        );
      }
    }
  }

  fields() {
    return {
      name: {
        name: "name",
        type: "text",
        label: "Name",
        required: true,
        placeholder: `e.g. MyCity Badminton ${dayjs().format("YYYY")}`,
        description: "The name of the tournament. You can add multiple competitions to a tournament later.",
        group: "basic",
      },
      registration_closes_at: {
        name: "registration_closes_at",
        type: "datetime",
        label: "Entry deadline",
        required: true,
        group: "schedule",
      },
      starts_at: {
        name: "starts_at",
        type: "datetime",
        includeTime: true,
        label: "Starting at",
        required: true,
        group: "schedule",
      },
      ends_at: {
        name: "ends_at",
        type: "datetime",
        includeTime: true,
        label: "Ends at",
        required: true,
        group: "schedule",
      },
      courts: {
        name: "courts",
        type: "number",
        label: "Courts",
        description: "The number of courts available for matches. You can adjust this later.",
        required: true,
        group: "basic",
        min: 1,
      },
      stand_down_minutes: {
        name: "stand_down_minutes",
        type: "number",
        label: "Stand-down between matches (minutes)",
        description:
          "Minimum rest time before a player can be assigned to another match across all competitions. Set to 0 to disable.",
        required: true,
        group: "basic",
        min: 0,
      },
      status: {
        name: "status",
        type: "select",
        label: "Status",
        description:
          "Where this tournament is in its lifecycle (entries, registration day, in progress, or finished).",
        required: true,
        options: Object.entries(TournamentSessionStatus).map(([value, label]) => ({ value, label })),
        group: "basic",
      },
      banner_image_id: {
        name: "banner_image_id",
        type: "upload",
        variant: "image",
        label: "Banner image",
        group: "signups",
      },
      is_public: {
        name: "is_public",
        type: "checkbox",
        label: "Enable signups",
        group: "signups",
      },
      description: {
        name: "description",
        type: "html",
        label: "Description",
        group: "signups",
      },
      confirmation_message: {
        name: "confirmation_message",
        type: "html",
        label: "Confirmation message",
        description: "This message will be sent to players via email after you confirm their entry.",
        group: "signups",
      },
      payment_by_player_or_entry: {
        name: "payment_by_player_or_entry",
        type: "select",
        label: "Payment by",
        description:
          "Choose whether payment is collected per player or per entry. This is relevant for doubles events.",
        options: Object.entries(TournamentPaymentBy).map(([value, label]) => ({ value, label })),
        required: true,
        group: "payments",
      },
    } as const satisfies Record<string, FormFieldConfig>;
  }

  override findModel(id: string) {
    return TournamentSession.query().find(id);
  }

  override async saveRecord(session: TournamentSession) {
    session.account_id = Account.current!.id;
    session.created_by ||= AccountUser.current!.id;
    session.club_id ||= this.props.attributes.club_id;

    await session.save();
  }

  /**
   * Show the payment method modal for adding a new payment method
   * @param paymentType The type of payment method to add
   */
  async actionShowAddPaymentMethodModal(paymentType: string) {
    if (paymentType === "akahu-one-off") {
      const tournamentId = this.state.record.id;
      try {
        await requireMfaSession({
          reason: "Confirm with your authenticator app to add a pay by bank payment method.",
          returnPath: tournamentId ? `/tournament/${tournamentId}` : undefined,
        });
      } catch (error) {
        if (!isMfaSessionAbortError(error)) {
          messagesService.addMessage(
            error instanceof Error ? error.message : "Two-factor authentication failed",
            "error",
          );
        }
        return;
      }
    }
    this.state.selectedPaymentType = paymentType;
    this.state.selectedPaymentMethodId = null;
    this.state.showPaymentMethodModal = true;
  }

  /**
   * Show the payment method modal for editing an existing payment method
   * @param paymentMethodId The ID of the payment method to edit
   */
  async actionShowEditPaymentMethodModal(paymentMethodId: string) {
    const paymentMethod = this.state.paymentMethods.findById(paymentMethodId);
    if (!paymentMethod) return;

    if (paymentMethod.payment_type === "akahu-one-off") {
      const tournamentId = this.state.record.id;
      try {
        await requireMfaSession({
          reason: "Confirm with your authenticator app to edit this pay by bank payment method.",
          returnPath: tournamentId ? `/tournament/${tournamentId}` : undefined,
        });
      } catch (error) {
        if (!isMfaSessionAbortError(error)) {
          messagesService.addMessage(
            error instanceof Error ? error.message : "Two-factor authentication failed",
            "error",
          );
        }
        return;
      }
    }

    this.state.selectedPaymentMethod = paymentMethod;
    this.state.selectedPaymentType = paymentMethod.payment_type || null;
    this.state.selectedPaymentMethodId = paymentMethodId;
    this.state.showPaymentMethodModal = true;
  }

  /**
   * Hide the payment method modal
   */
  actionHidePaymentMethodModal() {
    this.state.showPaymentMethodModal = false;
    this.state.selectedPaymentType = null;
    this.state.selectedPaymentMethodId = null;
  }

  /**
   * Save a payment method
   * @param paymentMethod The payment method to save
   */
  async actionSavePaymentMethod(paymentMethod: TournamentPaymentMethod) {
    // Add to the list if it's new
    if (paymentMethod.id && !this.state.paymentMethods.findById(paymentMethod.id)) {
      this.state.paymentMethods.push(paymentMethod);
    }

    this.syncPaymentMethodsToTournament();
    this.actionHidePaymentMethodModal();
  }

  /**
   * Remove a payment method
   * @param paymentMethodId The ID of the payment method to remove
   */
  async actionRemovePaymentMethod(paymentMethodId: string) {
    const paymentMethod = this.state.paymentMethods.findById(paymentMethodId);
    if (!paymentMethod) return;

    if (paymentMethod.payment_type === "akahu-one-off") {
      const tournamentId = this.state.record.id;
      try {
        await requireMfaSession({
          reason: "Confirm with your authenticator app to remove this pay by bank payment method.",
          returnPath: tournamentId ? `/tournament/${tournamentId}` : undefined,
        });
      } catch (error) {
        if (!isMfaSessionAbortError(error)) {
          messagesService.addMessage(
            error instanceof Error ? error.message : "Two-factor authentication failed",
            "error",
          );
        }
        return;
      }
    }

    paymentMethod.is_deleted = true;
    await paymentMethod.save();
    this.state.paymentMethods = new List(
      ...this.state.paymentMethods.filter((pm) => pm.id !== paymentMethodId),
    );
    this.syncPaymentMethodsToTournament();
  }

  /**
   * Get available payment methods for the account region.
   * NZ-only methods (Akahu, NZ bank transfer) require account region "nz".
   * Akahu also requires the akahu_tournament_payments account feature.
   */
  getAvailablePaymentMethods() {
    return getPaymentMethodsByRegion(Account.current!.region!).filter((method) => {
      if (method.id !== "akahu-one-off") return true;
      return this.hasAkahuTournamentPayments;
    });
  }
}
